import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect } from 'react';

import { endpoints, type BillingAgreementQuery, type Plan, type PlanRequestBody, type PurchaseBody } from './api';
import { usePermittedMutation } from './session';

/** Subscription and plan data hooks. */

export function useSubscription(enabled = true) {
    return useQuery({
        queryKey: ['subscription'],
        queryFn: () => endpoints.subscription(),
        // The overview asks only for someone who may read billing; for anyone
        // else the answer is a refusal with nothing to show.
        enabled,
    });
}

/**
 * Whole days until a trial ends, a day already begun counting as a day left,
 * and 0 once it has ended. One rule for the subscription page and the
 * overview, so they never give the same trial two different numbers.
 */
export function trialDaysLeft(trialEndsAt: string, now: Date = new Date()): number {
    const left = new Date(trialEndsAt).getTime() - now.getTime();
    return left <= 0 ? 0 : Math.ceil(left / 86_400_000);
}

export function usePlans(enabled = true) {
    return useQuery({
        queryKey: ['plans'],
        queryFn: () => endpoints.plans(),
        // A page that only names a package asks for the list only for someone
        // who may read billing; for anyone else the answer is a refusal.
        enabled,
    });
}

/**
 * The package to name where the account's own leaves a feature out (docs/11
 * §8): the other one with the least monthly price that includes the feature in
 * full, a package priced by offer only when no other does, and the list's own
 * order between equals. None when no other package includes it.
 */
export function packageIncluding(plans: Plan[], feature: string): Plan | undefined {
    return plans
        .filter((plan) => !plan.current && plan.featureLevels.some((level) => level.feature === feature && level.level === 'full'))
        .sort((a, b) => Number(a.customPricing) - Number(b.customPricing) || a.prices.monthly.total - b.prices.monthly.total)[0];
}

/**
 * The public price list, for a screen with no account yet: the sign-up page
 * reads the trial a new account gets from it. Reference data, so it is kept a
 * while rather than refetched on every visit.
 */
export function usePublicPlans() {
    return useQuery({
        queryKey: ['public-plans'],
        queryFn: () => endpoints.publicPlans(),
        staleTime: 5 * 60 * 1000,
    });
}

export function useRequestPlanChange() {
    return usePermittedMutation('billing.read', {
        mutationFn: (body: PlanRequestBody) => endpoints.requestPlanChange(body),
    });
}

/** How the platform can be paid right now: a transfer, a card, or neither yet (ADR 0016). */
export function usePaymentOptions() {
    return useQuery({
        queryKey: ['payment-options'],
        queryFn: () => endpoints.paymentOptions(),
    });
}

/** The account's payments, newest first. */
export function useBillingCharges() {
    return useQuery({
        queryKey: ['billing-charges'],
        queryFn: () => endpoints.billingCharges(),
    });
}

/** What the Abonelik page's address can say: the card payment a seller comes back from. */
export interface SubscriptionSearch {
    odeme?: string;
}

/** Reads the address's search, keeping only a reference shaped like one. */
export function validateSubscriptionSearch(search: Record<string, unknown>): SubscriptionSearch {
    return typeof search.odeme === 'string' && /^[A-Z0-9]{6,32}$/.test(search.odeme) ? { odeme: search.odeme } : {};
}

/**
 * The card payment a seller comes back from on the service's page (ADR 0016
 * §4). Its service's notification may arrive after the seller does, so while
 * it waits the account's payments are asked for again every few seconds, and
 * once it is paid the plan and the session — a lapse lifted — are read afresh.
 */
export function useReturnedPayment(reference: string | undefined) {
    const queryClient = useQueryClient();
    const charges = useQuery({
        queryKey: ['billing-charges'],
        queryFn: () => endpoints.billingCharges(),
        enabled: reference !== undefined,
        refetchInterval: (query) => (query.state.data?.charges.find((charge) => charge.reference === reference)?.status === 'pending' ? 3000 : false),
    });
    const returned = reference === undefined ? undefined : charges.data?.charges.find((charge) => charge.reference === reference);
    const status = returned?.status;
    useEffect(() => {
        if (status !== 'paid') return;
        queryClient.invalidateQueries({ queryKey: ['subscription'] });
        queryClient.invalidateQueries({ queryKey: ['session'] });
    }, [status, queryClient]);
    return returned;
}

/**
 * Buying a plan: a charge that waits for its payment, or, for a plan that
 * costs nothing, the plan itself at once. A card is paid on the service's own
 * page, whose address the answer carries.
 */
export function usePurchasePlan() {
    const queryClient = useQueryClient();
    return usePermittedMutation('billing.write', {
        mutationFn: (body: PurchaseBody) => endpoints.purchasePlan(body),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['billing-charges'] });
            queryClient.invalidateQueries({ queryKey: ['subscription'] });
            queryClient.invalidateQueries({ queryKey: ['session'] });
        },
    });
}

/**
 * The pre-information form and the distance contract a purchase would be made
 * on, written by the API for this package, period and way of paying
 * (ADR 0018). Never kept from an earlier read: what the seller accepts must be
 * what the API writes now, so every opening of the drawer reads it afresh.
 */
export function useBillingAgreement(query: BillingAgreementQuery, enabled: boolean) {
    return useQuery({
        queryKey: ['billing-agreement', query],
        queryFn: () => endpoints.billingAgreement(query),
        enabled,
        staleTime: 0,
        gcTime: 0,
        retry: false,
        // Not swapped under a reader who looks away and back; a text that
        // changed is met at the purchase, which asks for it to be read again.
        refetchOnWindowFocus: false,
    });
}

/** The texts a payment was accepted on, read when the seller opens them. */
export function useChargeAgreement(chargeId: string) {
    return useQuery({
        queryKey: ['billing-charge-agreement', chargeId],
        queryFn: () => endpoints.billingChargeAgreement(chargeId),
    });
}

/**
 * Opens a payment's invoice (ADR 0016 §9). Fetched when pressed rather than
 * rendered as a link: the address is signed for an hour, and one minted with
 * the page would be dead by the time anyone pressed it.
 */
export async function openChargeInvoice(chargeId: string) {
    const link = await endpoints.billingChargeInvoiceLink(chargeId);
    window.open(link.url, '_blank', 'noopener');
}

/**
 * Cancelling the package at the end of the paid period, and taking that back
 * while it still runs (ADR 0016 §6). The session follows: the lapse that a
 * cancellation ends in is read from it.
 */
export function useCancelSubscription() {
    const queryClient = useQueryClient();
    return usePermittedMutation('billing.write', {
        mutationFn: () => endpoints.cancelSubscription(),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['subscription'] });
            queryClient.invalidateQueries({ queryKey: ['session'] });
        },
    });
}

export function useResumeSubscription() {
    const queryClient = useQueryClient();
    return usePermittedMutation('billing.write', {
        mutationFn: () => endpoints.resumeSubscription(),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['subscription'] });
            queryClient.invalidateQueries({ queryKey: ['session'] });
        },
    });
}

/** Cancelling a payment still waiting. */
export function useCancelBillingCharge() {
    const queryClient = useQueryClient();
    return usePermittedMutation('billing.write', {
        mutationFn: (id: string) => endpoints.cancelBillingCharge(id),
        onSuccess: () => queryClient.invalidateQueries({ queryKey: ['billing-charges'] }),
    });
}
